import type { ComputedRef, MaybeRefOrGetter } from 'vue'

import type { KControlSize } from '../../types'

import { computed, inject, onScopeDispose, toValue, useId, watch } from 'vue'
import { K_FIELD_CONTEXT } from './context'

export interface KFieldControlProps {
    id?: string
    disabled?: boolean
    required?: boolean
    invalid?: boolean
    size?: KControlSize
}

export interface KFieldControl {
    controlId: ComputedRef<string>
    labelId: ComputedRef<string | undefined>
    describedBy: ComputedRef<string | undefined>
    invalid: ComputedRef<boolean>
    disabled: ComputedRef<boolean>
    required: ComputedRef<boolean>
    size: ComputedRef<KControlSize>
    controlAttrs: ComputedRef<{
        'id': string
        'aria-describedby': string | undefined
        'aria-invalid': true | undefined
        'required': true | undefined
        'disabled': true | undefined
    }>
}

export function useFieldControl(
    props: KFieldControlProps,
    options: { ownId?: MaybeRefOrGetter<boolean>, ownLabel?: MaybeRefOrGetter<boolean> } = {},
): KFieldControl {
    const field = inject(K_FIELD_CONTEXT, null)
    const generatedId = useId()
    let unregisterOwnLabel: (() => void) | undefined

    watch(
        () => toValue(options.ownLabel) ?? false,
        (ownsLabel) => {
            unregisterOwnLabel?.()
            unregisterOwnLabel = ownsLabel ? field?.registerOwnLabel() : undefined
        },
        { immediate: true },
    )
    onScopeDispose(() => unregisterOwnLabel?.())

    const controlId = computed(() => props.id
        ?? (!toValue(options.ownId) ? field?.controlId.value : undefined)
        ?? generatedId)
    const labelId = computed(() => field?.labelId.value)
    const describedBy = computed(() => field?.describedBy.value)
    const invalid = computed(() => props.invalid ?? field?.invalid.value ?? false)
    const disabled = computed(() => field?.nativeDisabled.value
        || (props.disabled ?? field?.disabled.value ?? false))
    const required = computed(() => props.required ?? field?.required.value ?? false)
    const size = computed(() => props.size ?? field?.size.value ?? 'md')
    const controlAttrs = computed(() => ({
        'id': controlId.value,
        'aria-describedby': describedBy.value,
        'aria-invalid': invalid.value || undefined,
        'required': required.value || undefined,
        'disabled': disabled.value || undefined,
    }))

    return {
        controlId,
        labelId,
        describedBy,
        invalid,
        disabled,
        required,
        size,
        controlAttrs,
    }
}
